import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./searchable-select";
import { FormDialog, Field } from "./form-dialog";
import { lookupValue, isoDateNotPast } from "@/lib/validators";

const schema = z.object({
  gallery: lookupValue("Pick a gallery"),
  stage: z.enum(["cull", "edit", "retouch", "review", "done"]),
  due: isoDateNotPast,
  imageCount: z.coerce.number().int("Whole numbers only").min(0).max(10_000),
});
type Values = z.infer<typeof schema>;

export function NewEditingDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { gallery: "", stage: "cull", due: "", imageCount: 0 },
  });
  const [galleryLabel, setGalleryLabel] = useState("");
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> Assign task</Button>}
      title="Assign editing task"
      description="Queue a gallery into the post-production pipeline."
      submitLabel="Assign task"
      isDirty={isDirty}
      onReset={() => { reset(); setGalleryLabel(""); }}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Task assigned", { description: `${v.gallery} → ${v.stage}` });
          resolve();
        }, () => reject())();
      })}
    >
      <Field label="Gallery" error={errors.gallery?.message}>
        <SearchableSelect
          resource="galleries"
          value={watch("gallery")}
          selectedLabel={galleryLabel}
          onChange={(v, label) => {
            setValue("gallery", v, { shouldValidate: true, shouldDirty: true });
            setGalleryLabel(label);
          }}
          placeholder="Select gallery"
          searchPlaceholder="Search galleries…"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Stage" error={errors.stage?.message}>
          <Select value={watch("stage")} onValueChange={(v) => setValue("stage", v as Values["stage"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cull">Cull</SelectItem>
              <SelectItem value="edit">Edit</SelectItem>
              <SelectItem value="retouch">Retouch</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Due date" htmlFor="ed-due" error={errors.due?.message}>
          <Input id="ed-due" type="date" {...register("due")} />
        </Field>
        <Field label="# Images" htmlFor="ed-count" error={errors.imageCount?.message}>
          <Input id="ed-count" type="number" min={0} {...register("imageCount")} />
        </Field>
      </div>
    </FormDialog>
  );
}
